// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useMemo } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  getPresignedUrlsWithFolder,
  handleUpload,
  MAX_FILE_SIZE_S3_ENDPOINT,
  validateFiles,
} from '@/lib/files/file-upload-helpers';
import { UploadFilesForm } from './upload-files-form';
import { type ShortFileProp } from '@/types/s3-file';
import type { File as S3File } from '@/models';
import { UserPlan } from '@/models/user';
import { toast } from 'sonner';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { API_BASE } from '@/config';

type UploadFilesFormProps = {
  folderName: string;
  onUploadSuccess: () => void;
  onFilesSaved: (files: S3File[]) => void;
  showUpgrade?: boolean;
};

export function UploadFilesS3PresignedUrl({
  folderName,
  onUploadSuccess,
  onFilesSaved,
  showUpgrade = true,
}: UploadFilesFormProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { user } = useAuth();
  const [plan, setPlan] = useState(UserPlan.FREE);
  // const { plan, loading } = useUserPlan();

  const maxFileSize = useMemo(() => {
    switch (plan) {
      case UserPlan.FREE:
        return 5;

      case UserPlan.BASIC:
        return 10;

      case UserPlan.OPTIMIM:
        return 50;

      case UserPlan.EXTENDED:
        return MAX_FILE_SIZE_S3_ENDPOINT;

      default:
        return 5;
    }
  }, [plan]);

  useEffect(() => {
    if (!user) {
      return;
    }
    fetch(`${API_BASE}/api/users/${user.id}`)
      .then((res) => res.json())
      .then((data) => {
        setPlan(data.plan);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [user]);

  const uploadToServer = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isLoading) return;

    const files = fileInputRef.current?.files ? Array.from(fileInputRef.current.files) : [];

    if (!files.length) {
      toast.error('Выберите файл');
      return;
    }

    const filesInfo: ShortFileProp[] = files.map((file) => ({
      originalFileName: file.name,
      fileSize: file.size,
    }));

    const filesValidationResult = validateFiles(filesInfo, maxFileSize);

    if (!filesValidationResult.success) {
      toast.error(filesValidationResult.message);

      if (showUpgrade && plan !== UserPlan.EXTENDED) {
        toast.info(`Тариф ${plan} ограничен ${maxFileSize}MB`);
      }

      return;
    }

    try {
      setIsLoading(true);

      const presignedUrls = await getPresignedUrlsWithFolder(
        filesInfo,
        folderName
      );

      const progressArray = new Array(files.length).fill(0);
      setProgress(0);

      const onProgress = (fileIndex: number, progressEvent: ProgressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        updateOverallProgress(fileIndex, percent);
      };

      const updateOverallProgress = (
        fileIndex: number,
        fileProgress: number
      ) => {
        const updatedProgress = progressArray.map((progress, index) =>
          index === fileIndex ? fileProgress : progress
        );
        setProgress(updatedProgress.reduce((a, b) => a + b) / files.length);
      };

      const savedFilesRaw = await handleUpload({
        files,
        presignedUrls,
        onProgress,
        onUploadSuccess,
      });

      onFilesSaved(savedFilesRaw);
    } catch (error) {
      console.error('Upload error:', error);
      // TODO: translate
      toast.error('Failed to upload files');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <UploadFilesForm
        isLoading={isLoading}
        fileInputRef={fileInputRef}
        uploadToServer={uploadToServer}
        maxFileSize={maxFileSize}
        showUpgrade={showUpgrade}
      />
      {isLoading && (
        <div className="my-4 flex items-center justify-center">
          <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-2.5 rounded-full bg-blue-600 dark:bg-blue-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}
    </>
  );
}
